import React, { useState, useEffect } from 'react'
import { supabase } from '../supabase'

const INCOME_CATS = ['Limpieza residencial','Limpieza comercial','Limpieza Airbnb','Limpieza Realtor','Depósito','Otro ingreso']
const EXPENSE_CATS = ['Suministros de limpieza','Gasolina','Equipo','Publicidad','Seguro','Nómina','Renta','Otro gasto']
const MONTHS_ES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre']
const MONTHS_SHORT = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic']

// Quarters: Q1=Jan-Mar, Q2=Apr-Jun, Q3=Jul-Sep, Q4=Oct-Dec
const QUARTERS = [
  { label:'Q1', months:[0,1,2], name:'Enero – Marzo' },
  { label:'Q2', months:[3,4,5], name:'Abril – Junio' },
  { label:'Q3', months:[6,7,8], name:'Julio – Septiembre' },
  { label:'Q4', months:[9,10,11], name:'Octubre – Diciembre' },
]

function getQuarter(month) { return Math.floor(month/3) }
function quarterBounds(year, q) {
  const start = `${year}-${String(QUARTERS[q].months[0]+1).padStart(2,'0')}-01`
  const lastMonth = QUARTERS[q].months[2]
  const lastDay = new Date(year, lastMonth+1, 0).getDate()
  const end = `${year}-${String(lastMonth+1).padStart(2,'0')}-${lastDay}`
  return { start, end }
}
function monthBounds(year, month) {
  const start = `${year}-${String(month+1).padStart(2,'0')}-01`
  const end = new Date(year, month+1, 0).toISOString().slice(0,10)
  return { start, end }
}

export default function Financials() {
  const now = new Date()
  const [year, setYear] = useState(now.getFullYear())
  const [quarter, setQuarter] = useState(getQuarter(now.getMonth()))
  const [month, setMonth] = useState(now.getMonth())
  const [drillLevel, setDrillLevel] = useState('quarter') // 'quarter' | 'month' | 'week'
  const [entries, setEntries] = useState([])
  const [allYearEntries, setAllYearEntries] = useState([])
  const [showAdd, setShowAdd] = useState(false)
  const [form, setForm] = useState({ type:'income', category:'Limpieza residencial', amount:'', description:'', entry_date: now.toISOString().slice(0,10) })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [tab, setTab] = useState('overview')

  useEffect(() => { fetchData() }, [year, quarter, month, drillLevel])

  async function fetchData() {
    setLoading(true)
    let start, end
    if (drillLevel === 'quarter') {
      const b = quarterBounds(year, quarter); start = b.start; end = b.end
    } else if (drillLevel === 'week') {
      const b = monthBounds(year, month); start = b.start; end = b.end
    } else {
      const b = monthBounds(year, month); start = b.start; end = b.end
    }
    const [{ data: entries }, { data: yearData }] = await Promise.all([
      supabase.from('financials').select('*').gte('entry_date', start).lte('entry_date', end).order('entry_date', { ascending: false }),
      supabase.from('financials').select('*').gte('entry_date', `${year}-01-01`).lte('entry_date', `${year}-12-31`),
    ])
    setEntries(entries || [])
    setAllYearEntries(yearData || [])
    setLoading(false)
  }

  async function addEntry() {
    if (!form.amount || !form.category) return
    setSaving(true)
    const { data } = await supabase.from('financials').insert([{
      type: form.type, category: form.category,
      amount: parseFloat(form.amount), description: form.description, entry_date: form.entry_date,
    }]).select().single()
    if (data) setEntries(prev => [data, ...prev])
    setShowAdd(false)
    setSaving(false)
  }

  const [editingEntry, setEditingEntry] = useState(null) // { id, amount, description }

  async function deleteEntry(id) {
    if (!window.confirm('¿Eliminar esta entrada?')) return
    await supabase.from('financials').delete().eq('id', id)
    setEntries(prev => prev.filter(e => e.id !== id))
  }

  async function saveEdit() {
    if (!editingEntry) return
    await supabase.from('financials').update({
      amount: parseFloat(editingEntry.amount),
      description: editingEntry.description,
    }).eq('id', editingEntry.id)
    setEntries(prev => prev.map(e => e.id===editingEntry.id ? {...e, amount:parseFloat(editingEntry.amount), description:editingEntry.description} : e))
    setEditingEntry(null)
  }

  function generateQuarterlyPDF() {
    const q = QUARTERS[quarter]
    const income = entries.filter(e=>e.type==='income')
    const expenses = entries.filter(e=>e.type==='expense')
    const totalIncome = income.reduce((s,e)=>s+parseFloat(e.amount||0),0)
    const totalExpenses = expenses.reduce((s,e)=>s+parseFloat(e.amount||0),0)
    const profit = totalIncome - totalExpenses

    // Group by category
    const incByCat = {}, expByCat = {}
    income.forEach(e=>{ incByCat[e.category]=(incByCat[e.category]||0)+parseFloat(e.amount||0) })
    expenses.forEach(e=>{ expByCat[e.category]=(expByCat[e.category]||0)+parseFloat(e.amount||0) })

    // Group by month
    const byMonth = {}
    QUARTERS[quarter].months.forEach(m => {
      byMonth[m] = {
        income: entries.filter(e=>e.type==='income'&&new Date(e.entry_date+'T12:00:00').getMonth()===m).reduce((s,e)=>s+parseFloat(e.amount||0),0),
        expense: entries.filter(e=>e.type==='expense'&&new Date(e.entry_date+'T12:00:00').getMonth()===m).reduce((s,e)=>s+parseFloat(e.amount||0),0),
      }
    })

    const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8">
<title>Reporte Trimestral ${q.label} ${year}</title>
<style>
  body { font-family:Arial,sans-serif; padding:32px; color:#202124; max-width:750px; margin:0 auto; }
  h1 { font-size:24px; color:#e8547a; margin-bottom:4px; }
  .sub { color:#80868b; font-size:13px; margin-bottom:24px; }
  .divider { height:2px; background:#e8547a; margin:20px 0; }
  .kpi-row { display:flex; gap:16px; margin-bottom:24px; }
  .kpi { flex:1; border:1px solid #e3e5e8; border-radius:10px; padding:14px; }
  .kpi-val { font-size:24px; font-weight:700; }
  .kpi-lbl { font-size:11px; color:#80868b; margin-top:4px; }
  .green { color:#1d9e75; } .red { color:#e24b4a; } .blue { color:#185fa5; }
  table { width:100%; border-collapse:collapse; font-size:12px; margin-bottom:20px; }
  th { background:#e8547a; color:white; padding:8px 10px; text-align:left; }
  td { padding:7px 10px; border-bottom:1px solid #e3e5e8; }
  tr:nth-child(even) td { background:#fdf0f3; }
  .total td { font-weight:700; background:#fdeef3; border-top:2px solid #e8547a; }
  h3 { font-size:14px; margin:20px 0 8px; color:#333; }
  .note { background:#faeeda; padding:12px; border-radius:8px; font-size:12px; color:#854f0b; margin-top:20px; }
  @media print { body { padding:16px; } }
</style>
</head><body>
  <h1>🌸 Kitty's Cleaning Services</h1>
  <div class="sub">Reporte Financiero Trimestral — ${q.label} ${year} (${q.name})</div>
  <div class="divider"></div>

  <div class="kpi-row">
    <div class="kpi"><div class="kpi-val green">$${totalIncome.toLocaleString('en-US',{minimumFractionDigits:2})}</div><div class="kpi-lbl">Ingresos totales</div></div>
    <div class="kpi"><div class="kpi-val red">$${totalExpenses.toLocaleString('en-US',{minimumFractionDigits:2})}</div><div class="kpi-lbl">Gastos totales</div></div>
    <div class="kpi"><div class="kpi-val ${profit>=0?'blue':'red'}">$${profit.toLocaleString('en-US',{minimumFractionDigits:2})}</div><div class="kpi-lbl">Ganancia neta</div></div>
  </div>

  <h3>Resumen por mes</h3>
  <table>
    <thead><tr><th>Mes</th><th>Ingresos</th><th>Gastos</th><th>Neto</th></tr></thead>
    <tbody>
      ${QUARTERS[quarter].months.map(m=>`
        <tr>
          <td>${MONTHS_ES[m]}</td>
          <td class="green">$${(byMonth[m]?.income||0).toFixed(2)}</td>
          <td class="red">$${(byMonth[m]?.expense||0).toFixed(2)}</td>
          <td class="${(byMonth[m]?.income||0)-(byMonth[m]?.expense||0)>=0?'blue':'red'}">$${((byMonth[m]?.income||0)-(byMonth[m]?.expense||0)).toFixed(2)}</td>
        </tr>`).join('')}
      <tr class="total">
        <td>TOTAL</td>
        <td class="green">$${totalIncome.toFixed(2)}</td>
        <td class="red">$${totalExpenses.toFixed(2)}</td>
        <td class="${profit>=0?'blue':'red'}">$${profit.toFixed(2)}</td>
      </tr>
    </tbody>
  </table>

  <h3>Ingresos por categoría</h3>
  <table>
    <thead><tr><th>Categoría</th><th>Monto</th><th>% del total</th></tr></thead>
    <tbody>
      ${Object.entries(incByCat).sort((a,b)=>b[1]-a[1]).map(([cat,amt])=>`
        <tr><td>${cat}</td><td class="green">$${amt.toFixed(2)}</td><td>${totalIncome>0?((amt/totalIncome)*100).toFixed(1):0}%</td></tr>`).join('')}
    </tbody>
  </table>

  <h3>Gastos por categoría</h3>
  <table>
    <thead><tr><th>Categoría</th><th>Monto</th><th>% del total</th></tr></thead>
    <tbody>
      ${Object.entries(expByCat).sort((a,b)=>b[1]-a[1]).map(([cat,amt])=>`
        <tr><td>${cat}</td><td class="red">$${amt.toFixed(2)}</td><td>${totalExpenses>0?((amt/totalExpenses)*100).toFixed(1):0}%</td></tr>`).join('')}
    </tbody>
  </table>

  <h3>Todas las transacciones</h3>
  <table>
    <thead><tr><th>Fecha</th><th>Tipo</th><th>Categoría</th><th>Descripción</th><th>Monto</th></tr></thead>
    <tbody>
      ${entries.map(e=>`
        <tr>
          <td>${new Date(e.entry_date+'T12:00:00').toLocaleDateString('es-MX',{month:'short',day:'numeric'})}</td>
          <td>${e.type==='income'?'Ingreso':'Gasto'}</td>
          <td>${e.category}</td>
          <td>${e.description||'—'}</td>
          <td class="${e.type==='income'?'green':'red'}">${e.type==='income'?'+':'-'}$${parseFloat(e.amount).toFixed(2)}</td>
        </tr>`).join('')}
    </tbody>
  </table>

  <div class="note">
    📋 Este reporte cubre el período ${q.name} ${year}.<br>
    Generado el ${new Date().toLocaleDateString('es-MX',{weekday:'long',year:'numeric',month:'long',day:'numeric'})}.<br>
    Kitty's Cleaning Services · 6427 Wilcox Ave Bell, CA 90201
  </div>
  <script>window.onload=()=>window.print()</script>
</body></html>`

    const win = window.open('','_blank')
    win.document.write(html)
    win.document.close()
  }

  // Computed values
  const income = entries.filter(e=>e.type==='income')
  const expenses = entries.filter(e=>e.type==='expense')
  const totalIncome = income.reduce((s,e)=>s+parseFloat(e.amount||0),0)
  const totalExpenses = expenses.reduce((s,e)=>s+parseFloat(e.amount||0),0)
  const profit = totalIncome - totalExpenses

  // Year totals by quarter for overview
  const yearByQ = QUARTERS.map((q,qi) => {
    const qEntries = allYearEntries.filter(e => {
      const m = new Date(e.entry_date+'T12:00:00').getMonth()
      return q.months.includes(m)
    })
    return {
      label: q.label,
      income: qEntries.filter(e=>e.type==='income').reduce((s,e)=>s+parseFloat(e.amount||0),0),
      expense: qEntries.filter(e=>e.type==='expense').reduce((s,e)=>s+parseFloat(e.amount||0),0),
    }
  })

  // By category
  function byCategory(list) {
    const groups = {}
    list.forEach(e => { groups[e.category]=(groups[e.category]||0)+parseFloat(e.amount||0) })
    return Object.entries(groups).sort((a,b)=>b[1]-a[1])
  }

  const periodLabel = drillLevel==='quarter'
    ? `${QUARTERS[quarter].label} ${year} — ${QUARTERS[quarter].name}`
    : `${MONTHS_ES[month]} ${year}`

  return (
    <div style={{display:'flex',height:'100%',overflow:'hidden',background:'#f8f9fa'}}>

      {/* LEFT SIDEBAR NAV */}
      <div style={{width:160,flexShrink:0,borderRight:'1px solid #e3e5e8',background:'#fff',overflowY:'auto',padding:'14px 0'}}>
        {/* Year */}
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'0 12px',marginBottom:10}}>
          <button style={s.nbtn} onClick={()=>setYear(y=>y-1)}>←</button>
          <span style={{fontSize:13,fontWeight:700,color:'#202124'}}>{year}</span>
          <button style={s.nbtn} onClick={()=>setYear(y=>y+1)}>→</button>
        </div>

        {QUARTERS.map((q,qi)=>(
          <div key={qi}>
            {/* Quarter row */}
            <div style={{display:'flex',alignItems:'center',gap:6,padding:'6px 12px',cursor:'pointer',
              background:quarter===qi&&drillLevel==='quarter'?'#fdeef3':'transparent',
              borderLeft:quarter===qi?'3px solid #e8547a':'3px solid transparent'}}
              onClick={()=>{ setQuarter(qi); setDrillLevel('quarter') }}>
              <span style={{fontSize:11,fontWeight:600,color:quarter===qi?'#e8547a':'#80868b'}}>{q.label}</span>
              <span style={{fontSize:10,color:'#bdc1c6',marginLeft:'auto'}}>{quarter===qi?'▾':'▸'}</span>
            </div>

            {/* Months — only show for selected quarter */}
            {quarter===qi&&(
              <div style={{paddingLeft:8}}>
                {q.months.map(m=>(
                  <div key={m}>
                    <div style={{display:'flex',alignItems:'center',gap:6,padding:'5px 12px',cursor:'pointer',
                      background:drillLevel!=='quarter'&&month===m?'#ede9fe':'transparent',
                      borderLeft:drillLevel!=='quarter'&&month===m?'3px solid #7F77DD':'3px solid transparent'}}
                      onClick={()=>{ setMonth(m); setDrillLevel('month') }}>
                      <span style={{fontSize:11,color:drillLevel!=='quarter'&&month===m?'#5b21b6':'#5f6368',fontWeight:drillLevel!=='quarter'&&month===m?500:400}}>
                        {MONTHS_SHORT[m]}
                      </span>
                    </div>

                    {/* Week toggle — only for selected month */}
                    {drillLevel!=='quarter'&&month===m&&(
                      <div style={{display:'flex',alignItems:'center',gap:6,padding:'4px 12px 4px 20px',cursor:'pointer',
                        background:drillLevel==='week'?'#f5f3ff':'transparent'}}
                        onClick={()=>setDrillLevel(drillLevel==='week'?'month':'week')}>
                        <span style={{fontSize:10,color:drillLevel==='week'?'#7F77DD':'#9aa0a6'}}>
                          {drillLevel==='week'?'▾':'▸'} Por semana
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}

        {/* Divider */}
        <div style={{borderTop:'1px solid #e3e5e8',margin:'12px 0'}}/>

        {/* Actions */}
        <div style={{padding:'0 12px',display:'flex',flexDirection:'column',gap:6}}>
          <button style={{...s.addBtn,width:'100%'}} onClick={()=>setShowAdd(true)}>+ Agregar</button>
          <button style={{...s.pdfBtn,width:'100%',textAlign:'center'}} onClick={generateQuarterlyPDF}>📄 PDF</button>
        </div>
      </div>

      {/* MAIN CONTENT */}
      <div style={{flex:1,display:'flex',flexDirection:'column',overflow:'hidden'}}>

        {/* Sub-tabs */}
        <div style={{display:'flex',alignItems:'center',gap:4,padding:'8px 14px',borderBottom:'1px solid #e3e5e8',background:'#fff',flexShrink:0}}>
          <span style={{fontSize:12,fontWeight:600,color:'#80868b',marginRight:4}}>
            {drillLevel==='week'
              ? `${MONTHS_SHORT[month]} ${year} — Por semana`
              : drillLevel==='month'
              ? `${MONTHS_SHORT[month]} ${year}`
              : `${QUARTERS[quarter].label} ${year}`}
          </span>
          <div style={{marginLeft:'auto',display:'flex',gap:4}}>
            {[['overview','Resumen'],['income','Ingresos'],['expenses','Gastos']].map(([v,l])=>(
              <button key={v} style={{fontSize:11,padding:'4px 10px',border:'1px solid #e3e5e8',borderRadius:8,cursor:'pointer',
                background:tab===v?'#fdeef3':'#fff',color:tab===v?'#c03060':'#80868b',fontWeight:tab===v?500:400}}
                onClick={()=>setTab(v)}>{l}</button>
            ))}
          </div>
        </div>

        {/* Stats row */}
        <div style={{...s.stats, gridTemplateColumns:'repeat(3,1fr)', padding:'10px 14px'}}>
          <div style={s.statCard}>
            <div style={{fontSize:10,color:'#9aa0a6',marginBottom:3}}>{periodLabel}</div>
            <div style={{...s.statNum,color:'#1d9e75'}}>${totalIncome.toLocaleString('en-US',{minimumFractionDigits:2})}</div>
            <div style={s.statLbl}>Ingresos</div>
          </div>
          <div style={s.statCard}>
            <div style={{fontSize:10,color:'#9aa0a6',marginBottom:3}}>{periodLabel}</div>
            <div style={{...s.statNum,color:'#e24b4a'}}>${totalExpenses.toLocaleString('en-US',{minimumFractionDigits:2})}</div>
            <div style={s.statLbl}>Gastos</div>
          </div>
          <div style={s.statCard}>
            <div style={{fontSize:10,color:'#9aa0a6',marginBottom:3}}>{periodLabel}</div>
            <div style={{...s.statNum,color:profit>=0?'#185fa5':'#e24b4a'}}>${profit.toLocaleString('en-US',{minimumFractionDigits:2})}</div>
            <div style={s.statLbl}>Ganancia neta</div>
          </div>
        </div>

        {/* Content */}
        <div style={s.content}>
          {loading ? <div style={s.loading}>Cargando...</div> : drillLevel==='week' ? (
            (() => {
              const b = monthBounds(year, month)
              const startD = new Date(b.start+'T12:00:00')
              const endD = new Date(b.end+'T12:00:00')
              const weeks = []
              let cur = new Date(startD)
              while(cur.getDay()!==1) cur.setDate(cur.getDate()-1)
              while(cur<=endD) {
                const wStart = new Date(cur)
                const wEnd = new Date(cur); wEnd.setDate(wEnd.getDate()+6)
                weeks.push({ start: wStart, end: wEnd })
                cur.setDate(cur.getDate()+7)
              }
              return (
                <div style={s.panel}>
                  <div style={s.panelTitle}>Semana por semana — {MONTHS_SHORT[month]} {year}</div>
                  <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                    <thead><tr>
                      <th style={{textAlign:'left',padding:'6px 8px',color:'#80868b',fontWeight:500,borderBottom:'1px solid #e3e5e8'}}>Semana</th>
                      <th style={{textAlign:'right',padding:'6px 8px',color:'#1d9e75',fontWeight:500,borderBottom:'1px solid #e3e5e8'}}>Ingresos</th>
                      <th style={{textAlign:'right',padding:'6px 8px',color:'#e24b4a',fontWeight:500,borderBottom:'1px solid #e3e5e8'}}>Gastos</th>
                      <th style={{textAlign:'right',padding:'6px 8px',color:'#185fa5',fontWeight:500,borderBottom:'1px solid #e3e5e8'}}>Neto</th>
                    </tr></thead>
                    <tbody>
                      {weeks.map((w,i)=>{
                        const ws = w.start.toISOString().slice(0,10)
                        const we = w.end.toISOString().slice(0,10)
                        const wIncome = entries.filter(e=>e.type==='income'&&e.entry_date>=ws&&e.entry_date<=we).reduce((s,e)=>s+parseFloat(e.amount||0),0)
                        const wExpense = entries.filter(e=>e.type==='expense'&&e.entry_date>=ws&&e.entry_date<=we).reduce((s,e)=>s+parseFloat(e.amount||0),0)
                        const wNet = wIncome - wExpense
                        const fmt = d => `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]}`
                        return (
                          <tr key={i} style={{borderBottom:'0.5px solid #e3e5e8'}}>
                            <td style={{padding:'8px',color:'#5f6368'}}>{fmt(w.start)} – {fmt(w.end)}</td>
                            <td style={{textAlign:'right',padding:'8px',color:'#1d9e75',fontWeight:500}}>${wIncome.toFixed(2)}</td>
                            <td style={{textAlign:'right',padding:'8px',color:'#e24b4a',fontWeight:500}}>${wExpense.toFixed(2)}</td>
                            <td style={{textAlign:'right',padding:'8px',color:wNet>=0?'#185fa5':'#e24b4a',fontWeight:600}}>${wNet.toFixed(2)}</td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )
            })()
          ) : (
            tab==='overview' ? (
              <div style={s.grid2}>
                <div style={s.panel}>
                  <div style={s.panelTitle}>Ingresos por categoría</div>
                  {byCategory(income).length===0?<div style={s.empty}>Sin ingresos</div>:
                    byCategory(income).map(([cat,amt])=>(
                      <div key={cat} style={s.catRow}>
                        <span style={s.catName}>{cat}</span>
                        <span style={{...s.catAmt,color:'#1d9e75'}}>${amt.toFixed(2)}</span>
                      </div>
                    ))
                  }
                </div>
                <div style={s.panel}>
                  <div style={s.panelTitle}>Gastos por categoría</div>
                  {byCategory(expenses).length===0?<div style={s.empty}>Sin gastos</div>:
                    byCategory(expenses).map(([cat,amt])=>(
                      <div key={cat} style={s.catRow}>
                        <span style={s.catName}>{cat}</span>
                        <span style={{...s.catAmt,color:'#e24b4a'}}>${amt.toFixed(2)}</span>
                      </div>
                    ))
                  }
                </div>
              </div>
            ) : (
              <div style={s.panel}>
                <div style={s.panelTitle}>{tab==='income'?'Ingresos':'Gastos'} — {periodLabel}</div>
                {(tab==='income'?income:expenses).length===0
                  ? <div style={s.empty}>Sin {tab==='income'?'ingresos':'gastos'} en este período</div>
                  : (tab==='income'?income:expenses).map(e=>{
                    const isEditing = editingEntry?.id===e.id
                    return (
                      <div key={e.id} style={s.entryRow}>
                        <div style={s.entryDate}>
                          <div style={{fontSize:14,fontWeight:600,color:'#202124'}}>{new Date(e.entry_date+'T12:00:00').getDate()}</div>
                          <div style={{fontSize:9,color:'#9aa0a6'}}>{MONTHS_SHORT[new Date(e.entry_date+'T12:00:00').getMonth()]}</div>
                        </div>
                        <div style={{flex:1}}>
                          {isEditing ? (
                            <input style={{...s.minput,fontSize:11,padding:'3px 6px'}} value={editingEntry.description}
                              onChange={e=>setEditingEntry(p=>({...p,description:e.target.value}))}/>
                          ) : (
                            <div style={{fontSize:12,color:'#444'}}>{e.description||e.category}</div>
                          )}
                          <div style={{fontSize:10,color:'#9aa0a6'}}>{e.category}</div>
                        </div>
                        {isEditing ? (
                          <div style={{display:'flex',gap:4,alignItems:'center'}}>
                            <input type="number" style={{...s.minput,width:70,fontSize:11,padding:'3px 6px'}} value={editingEntry.amount}
                              onChange={e=>setEditingEntry(p=>({...p,amount:e.target.value}))}/>
                            <button style={{fontSize:11,padding:'3px 8px',border:'none',background:'#e8547a',color:'#fff',borderRadius:6,cursor:'pointer'}} onClick={saveEdit}>✓</button>
                            <button style={{fontSize:11,padding:'3px 8px',border:'1px solid #e3e5e8',background:'#fff',color:'#80868b',borderRadius:6,cursor:'pointer'}} onClick={()=>setEditingEntry(null)}>✕</button>
                          </div>
                        ) : (
                          <div style={{display:'flex',gap:6,alignItems:'center'}}>
                            <div style={{fontSize:14,fontWeight:600,color:tab==='income'?'#1d9e75':'#e24b4a',cursor:'pointer'}}
                              onClick={()=>setEditingEntry({id:e.id,amount:e.amount,description:e.description||''})}>
                              ${parseFloat(e.amount).toFixed(2)}
                            </div>
                            <button style={{fontSize:10,padding:'2px 6px',border:'1px solid #e3e5e8',borderRadius:4,background:'#fff',color:'#bdc1c6',cursor:'pointer'}}
                              onClick={()=>deleteEntry(e.id)}>✕</button>
                          </div>
                        )}
                      </div>
                    )
                  })
                }
              </div>
            )
          )}
        </div>
      </div>


      {showAdd&&(
        <div style={s.overlay} onClick={()=>setShowAdd(false)}>
          <div style={s.modal} onClick={e=>e.stopPropagation()}>
            <div style={s.modalTitle}>Agregar transacción</div>
            <div style={s.mfield}>
              <label style={s.mlabel}>Tipo</label>
              <div style={{display:'flex',gap:8}}>
                {[['income','Ingreso'],['expense','Gasto']].map(([v,l])=>(
                  <button key={v} style={{flex:1,padding:'7px',border:'1px solid #e3e5e8',borderRadius:8,cursor:'pointer',
                    background:form.type===v?(v==='income'?'#1d9e75':'#e24b4a'):'#fff',
                    color:form.type===v?'#fff':'#5f6368',fontWeight:500}}
                    onClick={()=>setForm(f=>({...f,type:v,category:v==='income'?INCOME_CATS[0]:EXPENSE_CATS[0]}))}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
            <div style={s.mfield}>
              <label style={s.mlabel}>Categoría</label>
              <select style={s.minput} value={form.category} onChange={e=>setForm(f=>({...f,category:e.target.value}))}>
                {(form.type==='income'?INCOME_CATS:EXPENSE_CATS).map(c=><option key={c}>{c}</option>)}
              </select>
            </div>
            <div style={s.mfield}>
              <label style={s.mlabel}>Monto ($)</label>
              <input style={s.minput} type="number" min="0" step="0.01" value={form.amount} placeholder="0.00" onChange={e=>setForm(f=>({...f,amount:e.target.value}))}/>
            </div>
            <div style={s.mfield}>
              <label style={s.mlabel}>Descripción (opcional)</label>
              <input style={s.minput} value={form.description} placeholder="Ej: Jane Mora - limpieza" onChange={e=>setForm(f=>({...f,description:e.target.value}))}/>
            </div>
            <div style={s.mfield}>
              <label style={s.mlabel}>Fecha</label>
              <input style={s.minput} type="date" value={form.entry_date} onChange={e=>setForm(f=>({...f,entry_date:e.target.value}))}/>
            </div>
            <div style={{display:'flex',gap:8,marginTop:16}}>
              <button style={{flex:1,padding:'8px',border:'1px solid #e3e5e8',borderRadius:8,background:'#fff',color:'#80868b',cursor:'pointer'}} onClick={()=>setShowAdd(false)}>Cancelar</button>
              <button style={{flex:2,padding:'8px',border:'none',borderRadius:8,background:'#e8547a',color:'#fff',fontWeight:500,cursor:'pointer'}} onClick={addEntry} disabled={saving}>{saving?'Guardando...':'Guardar'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const s = {
  wrap:{display:'flex',height:'100%',overflow:'hidden',background:'#f8f9fa'},
  topbar:{display:'flex',alignItems:'center',gap:6,padding:'9px 14px',borderBottom:'1px solid #e3e5e8',background:'#fff',flexShrink:0,flexWrap:'wrap'},
  nbtn:{padding:'3px 8px',border:'1px solid #e3e5e8',borderRadius:6,background:'#f8f9fa',cursor:'pointer',fontSize:13,color:'#5f6368'},
  pdfBtn:{fontSize:11,padding:'5px 10px',border:'1px solid #dadce0',borderRadius:8,background:'#fff',color:'#e8547a',cursor:'pointer',fontWeight:500},
  addBtn:{fontSize:12,padding:'5px 12px',border:'none',borderRadius:8,background:'#e8547a',color:'#fff',fontWeight:500,cursor:'pointer'},
  stats:{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:8,padding:'10px 14px',borderBottom:'1px solid #e3e5e8',flexShrink:0},
  statCard:{background:'#fff',borderRadius:10,padding:'10px 12px',border:'1px solid #e3e5e8'},
  statNum:{fontSize:18,fontWeight:600,lineHeight:1},
  statLbl:{fontSize:10,color:'#80868b',marginTop:3},
  content:{flex:1,overflowY:'auto',padding:'12px 14px'},
  loading:{textAlign:'center',color:'#9aa0a6',padding:24,fontSize:13},
  grid2:{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12},
  panel:{background:'#fff',borderRadius:12,border:'1px solid #e3e5e8',padding:'14px 16px'},
  panelTitle:{fontSize:13,fontWeight:600,color:'#202124',marginBottom:10},
  empty:{fontSize:12,color:'#9aa0a6',padding:'12px 0'},
  catRow:{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'7px 0',borderBottom:'1px solid #f8f9fa'},
  catName:{fontSize:12,color:'#444'},
  catAmt:{fontSize:13,fontWeight:500},
  entryRow:{display:'flex',alignItems:'center',gap:10,padding:'8px 0',borderBottom:'1px solid #f8f9fa'},
  entryDate:{width:32,textAlign:'center',flexShrink:0},
  overlay:{position:'fixed',inset:0,background:'rgba(0,0,0,0.3)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:100},
  modal:{background:'#fff',borderRadius:14,padding:'20px 22px',width:340,boxShadow:'0 8px 32px rgba(0,0,0,0.15)'},
  modalTitle:{fontSize:15,fontWeight:600,color:'#202124',marginBottom:14},
  mfield:{marginBottom:12},
  mlabel:{fontSize:11,color:'#80868b',display:'block',marginBottom:3},
  minput:{width:'100%',padding:'7px 9px',border:'1px solid #e3e5e8',borderRadius:8,fontSize:13,background:'#f8f9fa',color:'#202124'},
}
